import React, { useState } from 'react'

interface CommentFormProps {
  onSubmit: (body: string) => Promise<boolean>
  placeholder?: string
  submitLabel?: string
  onCancel?: () => void
  loading?: boolean
  autoFocus?: boolean
}

export const CommentForm: React.FC<CommentFormProps> = ({
  onSubmit,
  placeholder = 'Write a comment...',
  submitLabel = '[post comment]',
  onCancel,
  loading = false,
  autoFocus = false,
}) => {
  const [body, setBody] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!body.trim()) return
    const ok = await onSubmit(body.trim())
    if (ok) setBody('')
  }

  return (
    <>
      <style>{`
        .comment-form {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .comment-textarea {
          width: 100%;
          min-height: 80px;
          background-color: var(--bg-tertiary);
          border: 0.5px solid var(--border-color-solid);
          border-radius: var(--radius-md, 8px);
          color: var(--text-primary);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          font-size: 0.9rem;
          line-height: 1.6;
          padding: 10px 14px;
          resize: vertical;
          transition: border-color 0.15s ease;
          outline: none;
        }
        .comment-textarea::placeholder {
          color: var(--text-muted);
        }
        .comment-textarea:focus {
          border-color: var(--accent-blue);
        }
        .comment-form-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .comment-submit-btn {
          background: rgba(133, 183, 235, 0.12);
          border: 0.5px solid rgba(133, 183, 235, 0.35);
          color: var(--accent-blue);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.77rem;
          padding: 5px 14px;
          border-radius: var(--radius-sm, 4px);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .comment-submit-btn:hover:not(:disabled) {
          background: rgba(133, 183, 235, 0.22);
          border-color: rgba(133, 183, 235, 0.6);
        }
        .comment-submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .comment-cancel-btn {
          background: none;
          border: 0.5px solid var(--border-color-solid);
          color: var(--text-muted);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.77rem;
          padding: 5px 12px;
          border-radius: var(--radius-sm, 4px);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .comment-cancel-btn:hover {
          color: var(--text-secondary);
          border-color: var(--border-color-hover);
        }
        .comment-char-count {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.68rem;
          color: var(--text-muted);
          align-self: flex-start;
        }
        .comment-char-count.over {
          color: var(--accent-red);
        }
      `}</style>
      <form className="comment-form" onSubmit={handleSubmit}>
        <textarea
          className="comment-textarea"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          maxLength={2000}
          autoFocus={autoFocus}
          disabled={loading}
        />
        <div className="comment-form-actions">
          <span className={`comment-char-count${body.length > 1900 ? ' over' : ''}`}>
            {body.length}/2000
          </span>
          {onCancel && (
            <button
              type="button"
              className="comment-cancel-btn"
              onClick={onCancel}
              disabled={loading}
            >
              [cancel]
            </button>
          )}
          <button
            type="submit"
            className="comment-submit-btn"
            disabled={loading || !body.trim()}
          >
            {loading ? 'posting...' : submitLabel}
          </button>
        </div>
      </form>
    </>
  )
}
